import React, { useEffect, useRef } from "react";

const SCRIPT_SRC = "//js.hsforms.net/forms/shell.js";
let lastTargetId = 0;

interface HubSpotFormProps {
  portalId: string;
  formId: string;
  initialValues?: Record<string, string | undefined>;
}

type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const prefillField = (form: HTMLFormElement, name: string, value: string) => {
  const controls = Array.from(form.elements ?? []).filter(
    (el): el is FormControl =>
      (el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement) &&
      el.name === name,
  );
  for (const control of controls) {
    if (control instanceof HTMLInputElement && control.type === "radio") {
      control.checked = control.value === value;
      if (!control.checked) continue;
    } else {
      control.value = value;
    }
    control.dispatchEvent(new Event("input", { bubbles: true }));
  }
};

const HubSpotForm: React.FC<HubSpotFormProps> = ({
  portalId,
  formId,
  initialValues,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Read when the form is ready, so a new object with the same values does not rebuild the form.
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  useEffect(() => {
    let cancelled = false;
    const formKey = `${portalId}/${formId}`;

    const createForm = () => {
      const hbspt = window.hbspt;
      const container = containerRef.current;
      if (cancelled || !hbspt || !container) return;
      // Strict Mode replays this effect on the same element, and HubSpot renders asynchronously.
      if (container.dataset.hubspotForm === formKey) return;
      container.dataset.hubspotForm = formKey;
      // A fresh target per build, so a request still in flight for an older form renders nowhere.
      const target = document.createElement("div");
      lastTargetId += 1;
      target.id = `hubspot-form-${lastTargetId}`;
      container.innerHTML = "";
      container.appendChild(target);
      hbspt.forms.create({
        portalId,
        formId,
        target: `#${target.id}`,
        onFormReady: (form) => {
          const values = initialValuesRef.current ?? {};
          for (const field of Object.keys(values)) {
            prefillField(form, field, values[field] ?? "");
          }
        },
      });
    };

    if (window.hbspt) {
      createForm();
      return () => {
        cancelled = true;
      };
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (!script) {
      const created = document.createElement("script");
      created.src = SCRIPT_SRC;
      // A failed tag would otherwise be reused forever; dropping it lets the next mount retry.
      created.addEventListener("error", () => created.remove(), { once: true });
      document.body.appendChild(created);
      script = created;
    }
    script.addEventListener("load", createForm);
    const loading = script;
    return () => {
      cancelled = true;
      loading.removeEventListener("load", createForm);
    };
  }, [portalId, formId]);

  return <div ref={containerRef} />;
};

export default HubSpotForm;
